/**
 * Glass Preset — High-quality automotive window glass material.
 *
 * Uses MeshPhysicalMaterial **without transmission**. The see-through effect
 * is achieved via standard alpha `opacity`; `clearcoat` provides the hard
 * specular surface reflection typical of glass.
 *
 * Why no transmission?
 *   Three.js transmission triggers a separate background-sample render pass
 *   which — depending on geometry, face orientation, and depth layering —
 *   produces persistent black-edge artifacts on thin automotive glass panels.
 *   A simple opacity + clearcoat approach avoids the extra pass entirely and
 *   delivers a clean, artefact-free glass look.
 *
 * Four tint presets mimic common automotive glass tinting levels:
 *   clear       — nearly-transparent windscreen (default)
 *   toned-light — slight privacy tint
 *   toned-mid   — medium tint (rear side windows)
 *   toned-dark  — heavy privacy glass (limo tint)
 *
 * Callers can also provide custom `colorCode` and/or `transparency` (0→1)
 * values via factory options to override the preset defaults.
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 */

import * as THREE from 'three';
import type { GlassType } from '../../api/types';
import { devWarn } from '../../utils/devLog';

export type { GlassType };

// ---------------------------------------------------------------------------
// PBR value definitions
// ---------------------------------------------------------------------------

export interface GlassValues {
  /** Base tint color (hex string). */
  color: string;
  /** Alpha opacity. 0 = fully transparent, 1 = fully opaque. */
  opacity: number;
  /** Surface roughness. 0 = mirror-smooth, 1 = fully diffuse. */
  roughness: number;
  /** Always 0 — glass is a dielectric. */
  metalness: number;
  /** Clearcoat layer for surface specularity. */
  clearcoat: number;
  /** Clearcoat surface roughness. */
  clearcoatRoughness: number;
}

// ---------------------------------------------------------------------------
// Preset Definitions
// ---------------------------------------------------------------------------

/**
 * Calibrated PBR values for automotive glass tint levels.
 *
 * - `opacity` controls how opaque the glass appears (0 = invisible, 1 = solid).
 * - `color` tints the visible glass surface.
 * - `clearcoat` adds the hard surface reflection layer typical of glass.
 * - `transmission` is intentionally NOT used — see file header.
 */
export const GLASS_PRESETS: Record<GlassType, GlassValues> = {
  clear: {
    color:             '#000000', // #f0f2f4
    opacity:           0.3,
    roughness:         0.05,
    metalness:         0.0,
    clearcoat:         1.0,
    clearcoatRoughness: 0.0,
  },
  'toned-light': {
    color:             '#000000', // #b5b7ba
    opacity:           0.5,
    roughness:         0.05,
    metalness:         0.0,
    clearcoat:         1.0,
    clearcoatRoughness: 0.0,
  },
  'toned-mid': {
    color:             '#000000', // #97999c
    opacity:           0.7,
    roughness:         0.05,
    metalness:         0.0,
    clearcoat:         1.0,
    clearcoatRoughness: 0.0,
  },
  'toned-dark': {
    color:             '#000000', //#6e7073
    opacity:           0.9,
    roughness:         0.05,
    metalness:         0.0,
    clearcoat:         1.0,
    clearcoatRoughness: 0.0,
  },
};

// ---------------------------------------------------------------------------
// Factory options
// ---------------------------------------------------------------------------

export interface GlassFactoryOptions {
  /** Tint preset. Default: 'clear'. */
  type?: GlassType | string;
  /** Override tint color (hex). Takes precedence over the preset colour. */
  colorCode?: string;
  /** Override transparency (0→1). 0 = opaque, 1 = fully see-through. Converted internally to opacity. */
  transparency?: number;
  /**
   * Opacity carried over from the original GLB material (Case 3).
   * Used when no explicit `type` or `transparency` is given — preserves the
   * original look while upgrading to high-quality glass PBR.
   * Set automatically by MaterialManager; callers typically don't set this.
   */
  originalOpacity?: number;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a new Glass MeshPhysicalMaterial.
 *
 * @param colorCode - Base tint color (hex). Can be overridden via opts.colorCode.
 * @param type      - Glass tint preset. Default: 'clear'.
 * @param opts      - Additional overrides (transparency, colorCode).
 */
export function createGlassMaterial(
  _colorCode: string,
  type: GlassType | string = 'clear',
  opts?: GlassFactoryOptions,
): THREE.MeshPhysicalMaterial {
  const preset = GLASS_PRESETS[type as GlassType] ?? (() => {
    devWarn('createGlassMaterial()', `unknown glass type "${type}" — falling back to "clear".`, {
      'Received type': type,
      'Valid types':   Object.keys(GLASS_PRESETS),
    });
    return GLASS_PRESETS.clear;
  })();

  // Resolve final opacity:
  //   1. Explicit opts.transparency always wins (Case 2) — inverted to opacity
  //   2. If an explicit type was given via opts (Case 1), use that preset's opacity
  //   3. Otherwise fall back to originalOpacity from the source material (Case 3)
  //   4. Last resort: preset.opacity (clear defaults)
  const hasExplicitType = opts?.type != null && GLASS_PRESETS[opts.type as GlassType] != null;
  const explicitPreset = hasExplicitType ? GLASS_PRESETS[opts!.type as GlassType] : null;
  // Color priority: explicit override → preset tint → passed-in colorCode (original)
  const finalColor  = opts?.colorCode ?? preset.color;
  const finalOpacity = opts?.transparency != null
    ? (1 - opts.transparency)
    : (explicitPreset ? explicitPreset.opacity : undefined)
    ?? opts?.originalOpacity
    ?? preset.opacity;

  const mat = new THREE.MeshPhysicalMaterial({
    color:              new THREE.Color(finalColor),
    // No transmission — avoids the separate background-sample render pass
    // that causes black-edge artifacts on thin glass panels.
    transmission:       0,
    opacity:            finalOpacity,
    transparent:        true,
    roughness:          preset.roughness,
    metalness:          preset.metalness,
    clearcoat:          preset.clearcoat,
    clearcoatRoughness: preset.clearcoatRoughness,
    side:               THREE.DoubleSide,
    // depthWrite: true is critical for greenhouse glass sorting correctness.
    depthWrite:         true,
    envMapIntensity:    1.5,
  });

  return mat;
}

// ---------------------------------------------------------------------------
// Preset value getter (for lerp targets / UI)
// ---------------------------------------------------------------------------

/**
 * Return the preset values for a given glass type.
 * Falls back to 'clear' for unknown type strings.
 */
export function getGlassPreset(type: GlassType | string): GlassValues {
  const preset = GLASS_PRESETS[type as GlassType];
  if (!preset) {
    devWarn('getGlassPreset()', `unknown glass type "${type}" — falling back to "clear".`, {
      'Received type': type,
      'Valid types':   Object.keys(GLASS_PRESETS),
    });
    return { ...GLASS_PRESETS.clear };
  }
  return { ...preset };
}
